import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { bouncy } from "ldrs";
import { toast } from "react-toastify";
import {
  getIeComplaintColoniesByCircle,
  getIeComplaintIssueTypes,
  resolveIeComplaintImageUrl,
} from "../../../Apis/ieComplaintApi";
import {
  DEFAULT_REPORT_ZONE_ID,
  DEFAULT_REPORT_ZONE_NAME,
  useIeComplaintStore,
} from "./IeComplaintStore/useIeComplaintStore";
import IeComplaintMapView from "./IeComplaintMapView";
import {
  IE_COMPLAINT_MESSAGES,
  buildIeComplaintValidationSchema,
  formatContactNoInput,
  formatLocationInput,
} from "./ieComplaintFormValidation";

bouncy.register();

const DEFAULT_MAP_CENTER = { lat: 28.613939, lng: 77.209021 };

const SELECT_MENU_PORTAL_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  minWidth: 76,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const sectionHeaderStyle = {
  backgroundColor: "rgb(235, 240, 247)",
  color: "#003366",
  padding: "8px 12px",
  borderRadius: "4px",
  fontWeight: 600,
  marginBottom: "16px",
};

const parseDateForInput = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.split("T")[0];
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split("T")[0];
  }
  return "";
};

const IMAGE_FILE_ACCEPT = "image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png";

const isValidImageFile = (file) => {
  if (!file) return true;
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
  return (
    allowedTypes.includes(file.type) || /\.(jpe?g|png)$/i.test(file.name || "")
  );
};

const AutoFillCurrentLocationCoords = ({
  currentLocation,
  isEditMode,
  latitude,
  longitude,
  setFieldValue,
}) => {
  useEffect(() => {
    if (isEditMode || !currentLocation) return;
    if (latitude || longitude) return;

    setFieldValue("latitude", currentLocation.lat.toFixed(6));
    setFieldValue("longitude", currentLocation.lng.toFixed(6));
  }, [currentLocation, isEditMode, latitude, longitude, setFieldValue]);

  return null;
};

const AddEditIeComplaintForm = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
  zones,
  wards,
  onZoneChange,
  defaultZoneId = DEFAULT_REPORT_ZONE_ID,
}) => {
  const {
    isSaving,
    saveComplaint,
    kpiCategoryOptions,
    statusOptions,
    complaints,
  } = useIeComplaintStore();
  const [locationLoading, setLocationLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [colonies, setColonies] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const isSubmittingRef = useRef(false);

  const loadColonies = async (wardId) => {
    if (!wardId) {
      setColonies([]);
      return;
    }

    try {
      const response = await getIeComplaintColoniesByCircle(wardId);
      setColonies(
        response.map((colony) => ({
          colonyId: colony.colonyId?.toString(),
          colonyName: colony.colonyName ?? "",
        }))
      );
    } catch (error) {
      console.error("Error fetching colonies:", error);
      setColonies([]);
    }
  };

  const loadIssueTypes = async (kpiTypeId) => {
    if (!kpiTypeId) {
      setIssueTypes([]);
      return;
    }

    try {
      const response = await getIeComplaintIssueTypes(kpiTypeId);
      setIssueTypes(
        response.map((type) => ({
          issueTypeId: (type.ieissueTypeId ?? type.issueTypeId)?.toString(),
          issueType: type.issueType ?? "",
        }))
      );
    } catch (error) {
      console.error("Error fetching issue types:", error);
      setIssueTypes([]);
    }
  };

  useEffect(() => {
    if (!showModal) return;

    if (isEditMode && editRow?.wardId) {
      loadColonies(editRow.wardId);
    } else if (!isEditMode) {
      setColonies([]);
    }
  }, [showModal, isEditMode, editRow?.wardId]);

  useEffect(() => {
    if (!showModal || isEditMode) return;

    if (!navigator.geolocation) return;

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationLoading(false);
      },
      () => setLocationLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [showModal, isEditMode]);

  useEffect(() => {
    if (!showModal) {
      setCurrentLocation(null);
    }
  }, [showModal]);

  const modalWards = isEditMode
    ? wards.filter(
        (ward) =>
          ward.zoneId?.toString() === editRow?.zoneId?.toString() ||
          ward.zoneId === undefined
      )
    : wards;

  const getKpiValue = (row) => {
    if (!row) return "";
    const rawId = row.kpiTypeId;
    if (rawId !== undefined && rawId !== null && rawId !== "" && Number(rawId) > 0) {
      return String(rawId);
    }
    const match = kpiCategoryOptions.find((item) => item.kpiType === row.kpiCategory);
    return match ? String(match.kpiTypeId) : row.kpiCategory || "";
  };

  useEffect(() => {
    if (!showModal) return;

    if (isEditMode && editRow) {
      loadIssueTypes(getKpiValue(editRow));
    } else if (!isEditMode) {
      setIssueTypes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, isEditMode, editRow?.kpiTypeId, editRow?.kpiCategory]);

  const issueTypeSelectOptions = useMemo(() => {
    if (!isEditMode || !editRow) return issueTypes;

    const editId = editRow.issueTypeId;
    if (editId === undefined || editId === null || editId === "") return issueTypes;

    const idStr = String(editId);
    if (issueTypes.some((item) => String(item.issueTypeId) === idStr)) {
      return issueTypes;
    }

    return [
      {
        issueTypeId: editId,
        issueType: editRow.issueType || `Issue Type ${editId}`,
      },
      ...issueTypes,
    ];
  }, [isEditMode, editRow, issueTypes]);

  const getIssueTypeValue = (row) => {
    if (!row) return "";
    const rawId = row.issueTypeId;
    if (rawId !== undefined && rawId !== null && rawId !== "") {
      const idStr = String(rawId);
      if (issueTypeSelectOptions.some((item) => String(item.issueTypeId) === idStr)) {
        return idStr;
      }
    }
    if (row.issueType) {
      const match = issueTypeSelectOptions.find((item) => item.issueType === row.issueType);
      if (match) return String(match.issueTypeId);
    }
    return "";
  };

  const colonySelectOptions = useMemo(() => {
    if (!isEditMode || !editRow) return colonies;

    const editColonyId = editRow.colonyId;
    if (editColonyId === undefined || editColonyId === null || editColonyId === "") {
      return colonies;
    }

    const idStr = String(editColonyId);
    if (colonies.some((item) => String(item.colonyId) === idStr)) {
      return colonies;
    }

    return [
      {
        colonyId: idStr,
        colonyName: editRow.colonyName || `Colony ${editColonyId}`,
      },
      ...colonies,
    ];
  }, [colonies, editRow, isEditMode]);

  const getColonyValue = (row) => {
    if (!row) return "";
    if (row.colonyId !== undefined && row.colonyId !== null && row.colonyId !== "") {
      return String(row.colonyId);
    }
    const match = colonySelectOptions.find((item) => item.colonyName === row.colonyName);
    return match ? match.colonyId : "";
  };

  const existingClosingImageUrl = isEditMode
    ? resolveIeComplaintImageUrl(editRow?.closingImageUrl || editRow?.afterImage)
    : "";

  const initialValues = {
    contactNo: isEditMode ? editRow?.contactNo || "" : "",
    kpiCategory: isEditMode ? getKpiValue(editRow) : "",
    issueType: isEditMode ? getIssueTypeValue(editRow) : "",
    zoneId: isEditMode
      ? editRow?.zoneId || ""
      : defaultZoneId || DEFAULT_REPORT_ZONE_ID,
    wardId: isEditMode ? editRow?.wardId || "" : "",
    location: isEditMode ? editRow?.location || "" : "",
    complaintCreatedOn: isEditMode
      ? parseDateForInput(editRow?.complaintCreatedOn)
      : new Date().toISOString().split("T")[0],
    colonyName: isEditMode ? getColonyValue(editRow) : "",
    description: isEditMode ? editRow?.description || "" : "",
    latitude: isEditMode ? editRow?.latitude || "" : "",
    longitude: isEditMode ? editRow?.longitude || "" : "",
    status: isEditMode
      ? statusOptions.includes(editRow?.status)
        ? editRow.status
        : ""
      : "Open",
    uploadFile: null,
    modeOfReporting: isEditMode ? editRow?.modeOfReporting || "WEB" : "WEB",
    actionRemarks: isEditMode ? editRow?.actionRemarks || "" : "",
  };

  const editStatusOptions = statusOptions;

  const validationSchema = useMemo(
    () =>
      buildIeComplaintValidationSchema({
        isEditMode,
        statusOptions: editStatusOptions,
      }),
    [isEditMode, editStatusOptions]
  );

  const sortedKpiCategoryOptions = useMemo(
    () =>
      [...kpiCategoryOptions].sort((a, b) =>
        (a.kpiType || "").localeCompare(b.kpiType || "", undefined, { sensitivity: "base" })
      ),
    [kpiCategoryOptions]
  );

  const kpiCategorySelectOptions = useMemo(
    () =>
      sortedKpiCategoryOptions.map((category) => ({
        value: String(category.kpiTypeId),
        label: `${category.kpiCode ? `${category.kpiCode} - ` : ""}${category.kpiType}`,
      })),
    [sortedKpiCategoryOptions]
  );

  const issueTypeSelectFieldOptions = useMemo(
    () =>
      issueTypeSelectOptions.map((type) => ({
        value: String(type.issueTypeId),
        label: type.issueType,
      })),
    [issueTypeSelectOptions]
  );

  const colonySelectFieldOptions = useMemo(
    () =>
      colonySelectOptions.map((colony) => ({
        value: String(colony.colonyId),
        label: colony.colonyName,
      })),
    [colonySelectOptions]
  );

  const handleClose = () => {
    setShowModal(false);
  };

  const getZoneName = (zoneId) =>
    zones.find((zone) => zone.zoneId?.toString() === zoneId?.toString())?.zoneName || "";

  const getWardName = (wardId) => {
    const ward = wards.find(
      (item) =>
        item.wardId?.toString() === wardId?.toString() ||
        item.circleId?.toString() === wardId?.toString()
    );
    return ward?.wardNo || ward?.circleName || "";
  };

  const handleSubmit = async (values) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      await submitComplaint(values);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const submitComplaint = async (values) => {
    const selectedColony = colonies.find(
      (colony) => String(colony.colonyId) === String(values.colonyName)
    );

    if (!isEditMode) {
      const normalizedContact = (values.contactNo || "").trim();
      const normalizedLocation = (values.location || "").trim().toLowerCase();
      const normalizedColony = (selectedColony?.colonyName || values.colonyName || "")
        .trim()
        .toLowerCase();

      const isDuplicate = complaints.some((item) => {
        if (item.status === "Closed" || item.status === "Invalid") return false;
        return (
          (item.contactNo || "").trim() === normalizedContact &&
          (item.location || "").trim().toLowerCase() === normalizedLocation &&
          String(item.zoneId) === String(values.zoneId) &&
          String(item.wardId) === String(values.wardId) &&
          (item.colonyName || "").trim().toLowerCase() === normalizedColony
        );
      });

      if (isDuplicate) {
        toast.error(IE_COMPLAINT_MESSAGES.duplicateComplaint);
        return;
      }
    }

    const payload = {
      ...values,
      colonyId: selectedColony?.colonyId ?? values.colonyName,
      colonyName: selectedColony?.colonyName ?? values.colonyName,
      kpiTypeId: values.kpiCategory,
      issueTypeId: values.issueType,
      zoneName: getZoneName(values.zoneId),
      wardNo: getWardName(values.wardId),
      complaintCreatedOn: values.complaintCreatedOn
        ? `${values.complaintCreatedOn}T${new Date().toTimeString().slice(0, 5)}`
        : "",
      imageFile: values.uploadFile,
      imageUrl: values.uploadFile
        ? URL.createObjectURL(values.uploadFile)
        : isEditMode
          ? editRow?.imageUrl || ""
          : "",
    };

    delete payload.uploadFile;

    if (isEditMode) {
      payload.complaintId = editRow.complaintId;
      payload.complainantNo = editRow.complainantNo;
      payload.complaintCreatedOn = editRow.complaintCreatedOn || payload.complaintCreatedOn;
      payload.complaintClosureOn = editRow.complaintClosureOn || "";
      payload.actualDuration = editRow.actualDuration || "";
      payload.actionHistory = editRow.actionHistory || [];
      payload.wasClosed =
        editRow.isClosed || editRow.status === "Closed" || editRow.status === "Invalid";
      const useFormCoords = values.status === "Closed" || values.status === "Invalid";
      payload.closeTimeLatitude = useFormCoords
        ? values.latitude || editRow.closeTimeLatitude || editRow.cLat || ""
        : editRow.closeTimeLatitude || editRow.cLat || "";
      payload.closeTimeLongitude = useFormCoords
        ? values.longitude || editRow.closeTimeLongitude || editRow.cLng || ""
        : editRow.closeTimeLongitude || editRow.cLng || "";
      payload.closeTimeLocation = useFormCoords
        ? values.location || editRow.closeTimeLocation || editRow.cAddress || ""
        : editRow.closeTimeLocation || editRow.cAddress || "";
    }

    const result = await saveComplaint(payload, isEditMode);
    if (result?.success) {
      handleClose();
    }
  };

  return (
    <Modal
      size="lg"
      show={showModal}
      onHide={handleClose}
      aria-labelledby="ie-complaint-modal-title"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title id="ie-complaint-modal-title">
          {isEditMode ? "Edit Complaint" : "Add IE Complaint"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") handleClose();
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr className="mt-4" />
      <Modal.Body>
        <Formik
          key={
            isEditMode
              ? `edit-${editRow?.complaintId}-${colonySelectOptions.length}-${issueTypeSelectOptions.length}`
              : `add-${defaultZoneId}`
          }
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, setFieldTouched, setFieldError }) => {
            const availableWards = modalWards.filter(
              (ward) =>
                !values.zoneId ||
                ward.zoneId?.toString() === values.zoneId?.toString() ||
                ward.zoneId === undefined
            );

            const mapCenter =
              values.latitude && values.longitude
                ? {
                    lat: Number(values.latitude),
                    lng: Number(values.longitude),
                  }
                : currentLocation || DEFAULT_MAP_CENTER;

            return (
              <Form>
                <AutoFillCurrentLocationCoords
                  currentLocation={currentLocation}
                  isEditMode={isEditMode}
                  latitude={values.latitude}
                  longitude={values.longitude}
                  setFieldValue={setFieldValue}
                />
                <h6 className="mb-0" style={sectionHeaderStyle}>
                  Complaint Details
                </h6>
                <div className="row">
                  {isEditMode ? (
                    <div className="col-md-4 mb-3">
                      <label htmlFor="complainantNo" className="form-label">
                        Complaint No{" "}
                        <span style={{ color: "#EC1117" }}>*</span>
                      </label>
                      <input
                        id="complainantNo"
                        name="complainantNo"
                        className="form-control"
                        value={editRow?.complainantNo || ""}
                        placeholder="Enter Number"
                        disabled
                        readOnly
                      />
                    </div>
                  ) : null}
                  <div className="col-md-4 mb-3">
                    <label htmlFor="contactNo" className="form-label">
                      Complianant Contact No{" "}
                      <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Field
                      id="contactNo"
                      name="contactNo"
                      className="form-control"
                      placeholder="Enter 10 digit mobile no"
                      maxLength={10}
                      disabled={isEditMode}
                      readOnly={isEditMode}
                      onChange={(event) =>
                        setFieldValue("contactNo", formatContactNoInput(event.target.value))
                      }
                    />
                    <ErrorMessage className="required-message" name="contactNo" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="kpiCategory" className="form-label">
                      KPI Category <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Select
                      inputId="kpiCategory"
                      name="kpiCategory"
                      classNamePrefix="react-select"
                      options={kpiCategorySelectOptions}
                      value={
                        kpiCategorySelectOptions.find(
                          (option) => option.value === String(values.kpiCategory)
                        ) || null
                      }
                      onChange={(selected) => {
                        const kpiValue = selected ? selected.value : "";
                        setFieldValue("kpiCategory", kpiValue, true);
                        setFieldTouched("kpiCategory", true, false);
                        setFieldValue("issueType", "", false);
                        loadIssueTypes(kpiValue);
                      }}
                      placeholder="Select Category"
                      isDisabled={isEditMode}
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage className="required-message" name="kpiCategory" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="issueType" className="form-label">
                      Issue Type <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Select
                      inputId="issueType"
                      name="issueType"
                      classNamePrefix="react-select"
                      options={issueTypeSelectFieldOptions}
                      value={
                        issueTypeSelectFieldOptions.find(
                          (option) => option.value === String(values.issueType)
                        ) || null
                      }
                      onChange={(selected) => {
                        setFieldValue("issueType", selected ? selected.value : "", true);
                        setFieldTouched("issueType", true, false);
                      }}
                      placeholder="Select Issue Type"
                      isDisabled={isEditMode}
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage className="required-message" name="issueType" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="zoneId" className="form-label">
                      Zone <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Field
                      as="select"
                      id="zoneId"
                      name="zoneId"
                      className="form-select"
                      disabled={isEditMode}
                      onChange={(event) => {
                        setFieldValue("zoneId", event.target.value);
                        setFieldValue("wardId", "");
                        setFieldValue("colonyName", "");
                        setColonies([]);
                        onZoneChange?.(event.target.value);
                      }}
                    >
                      {zones.map((zone) => (
                        <option key={zone.zoneId} value={zone.zoneId}>
                          {zone.zoneName}
                        </option>
                      ))}
                      {!isEditMode &&
                      defaultZoneId &&
                      !zones.some(
                        (zone) => String(zone.zoneId) === String(defaultZoneId)
                      ) ? (
                        <option value={defaultZoneId}>
                          {DEFAULT_REPORT_ZONE_NAME}
                        </option>
                      ) : null}
                    </Field>
                    <ErrorMessage className="required-message" name="zoneId" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="wardId" className="form-label">
                      Ward <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Select
                      inputId="wardId"
                      name="wardId"
                      classNamePrefix="react-select"
                      options={availableWards.map((ward) => ({
                        value: String(ward.wardId ?? ward.circleId),
                        label: ward.wardNo || ward.circleName,
                      }))}
                      value={
                        availableWards
                          .map((ward) => ({
                            value: String(ward.wardId ?? ward.circleId),
                            label: ward.wardNo || ward.circleName,
                          }))
                          .find((option) => option.value === String(values.wardId)) || null
                      }
                      onChange={(selected) => {
                        const wardId = selected ? selected.value : "";
                        setFieldValue("wardId", wardId, true).then(() =>
                          setFieldTouched("wardId", true, true)
                        );
                        setFieldValue("colonyName", "");
                        loadColonies(wardId);
                      }}
                      placeholder="Select Ward"
                      isDisabled={isEditMode}
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage className="required-message" name="wardId" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="location" className="form-label">
                      Location <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Field
                      id="location"
                      name="location"
                      className="form-control"
                      placeholder="Enter Location"
                      maxLength={250}
                      disabled={isEditMode}
                      readOnly={isEditMode}
                      onChange={(event) =>
                        setFieldValue("location", formatLocationInput(event.target.value))
                      }
                    />
                    <ErrorMessage className="required-message" name="location" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="complaintCreatedOn" className="form-label">
                      Complaint Created On <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Field
                      id="complaintCreatedOn"
                      type="date"
                      name="complaintCreatedOn"
                      className="form-control"
                      max={new Date().toISOString().split("T")[0]}
                      disabled={isEditMode}
                      readOnly={isEditMode}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="complaintCreatedOn"
                      component="div"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="colonyName" className="form-label">
                      Colony Name <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Select
                      inputId="colonyName"
                      name="colonyName"
                      classNamePrefix="react-select"
                      options={colonySelectFieldOptions}
                      value={
                        colonySelectFieldOptions.find(
                          (option) => option.value === String(values.colonyName)
                        ) || null
                      }
                      onChange={(selected) => {
                        setFieldValue("colonyName", selected ? selected.value : "", true);
                        setFieldTouched("colonyName", true, false);
                      }}
                      placeholder="Select Colony Name"
                      isDisabled={isEditMode}
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage className="required-message" name="colonyName" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="latitude" className="form-label">
                      Latitude <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Field
                      id="latitude"
                      name="latitude"
                      className="form-control"
                      placeholder="Enter Latitude"
                      disabled={isEditMode}
                      readOnly={isEditMode}
                      onChange={(event) =>
                        setFieldValue(
                          "latitude",
                          event.target.value.replace(/[^\d.-]/g, "")
                        )
                      }
                    />
                    <ErrorMessage className="required-message" name="latitude" component="div" />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="longitude" className="form-label">
                      Longitude <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Field
                      id="longitude"
                      name="longitude"
                      className="form-control"
                      placeholder="Enter Longitude"
                      disabled={isEditMode}
                      readOnly={isEditMode}
                      onChange={(event) =>
                        setFieldValue(
                          "longitude",
                          event.target.value.replace(/[^\d.-]/g, "")
                        )
                      }
                    />
                    <ErrorMessage className="required-message" name="longitude" component="div" />
                  </div>
                  {isEditMode ? (
                    <div className="col-md-4 mb-3">
                      <label htmlFor="status" className="form-label">
                        Status <span style={{ color: "#EC1117" }}>*</span>
                      </label>
                      <Field as="select" id="status" name="status" className="form-select">
                        <option value="">Select Status</option>
                        {editStatusOptions.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage className="required-message" name="status" component="div" />
                    </div>
                  ) : null}
                  {isEditMode ? (
                    <div className="col-md-4 mb-3">
                      <label htmlFor="uploadFile" className="form-label">
                        Upload Closing Image
                      </label>
                      <input
                        id="uploadFile"
                        type="file"
                        className="form-control"
                        accept={IMAGE_FILE_ACCEPT}
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (file && !isValidImageFile(file)) {
                            event.target.value = "";
                            setFieldValue("uploadFile", null, false);
                            setFieldTouched("uploadFile", true, false);
                            setFieldError("uploadFile", IE_COMPLAINT_MESSAGES.invalidImageType);
                            return;
                          }
                          setFieldValue("uploadFile", file, true);
                          setFieldTouched("uploadFile", true, false);
                        }}
                      />
                      <ErrorMessage className="required-message" name="uploadFile" component="div" />
                      {!values.uploadFile && existingClosingImageUrl ? (
                        <div className="mt-2">
                          <img
                            src={existingClosingImageUrl}
                            alt="Closing complaint"
                            style={{
                              width: 72,
                              height: 72,
                              objectFit: "cover",
                              borderRadius: 4,
                              marginTop: 6,
                              border: "1px solid #E5E7EB",
                            }}
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="col-md-4 mb-3">
                      <label htmlFor="uploadFile" className="form-label">
                        Upload File
                      </label>
                      <input
                        id="uploadFile"
                        type="file"
                        className="form-control"
                        accept={IMAGE_FILE_ACCEPT}
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (file && !isValidImageFile(file)) {
                            event.target.value = "";
                            setFieldValue("uploadFile", null, false);
                            setFieldTouched("uploadFile", true, false);
                            setFieldError("uploadFile", IE_COMPLAINT_MESSAGES.invalidImageType);
                            return;
                          }
                          setFieldValue("uploadFile", file, true);
                          setFieldTouched("uploadFile", true, false);
                        }}
                      />
                      <ErrorMessage className="required-message" name="uploadFile" component="div" />
                    </div>
                  )}
                  {isEditMode ? (
                    <div className="col-md-4 mb-3">
                      <label htmlFor="actionRemarks" className="form-label">
                        Action Remarks <span style={{ color: "#EC1117" }}>*</span>
                      </label>
                      <Field
                        id="actionRemarks"
                        name="actionRemarks"
                        className="form-control"
                        placeholder="Enter Action Remarks"
                        maxLength={1000}
                      />
                      <ErrorMessage className="required-message" name="actionRemarks" component="div" />
                    </div>
                  ) : null}
                  <div className="col-md-12 mb-3">
                    <label htmlFor="description" className="form-label">
                      Description <span style={{ color: "#EC1117" }}>*</span>
                    </label>
                    <Field
                      as="textarea"
                      id="description"
                      name="description"
                      rows="3"
                      className="form-control"
                      placeholder="Enter Description"
                      maxLength={1000}
                      readOnly={isEditMode}
                    />
                    <ErrorMessage className="required-message" name="description" component="div" />
                  </div>
                </div>

                <h6 className="mb-0 mt-2" style={sectionHeaderStyle}>
                  Map View
                </h6>
                <IeComplaintMapView
                  latitude={values.latitude}
                  longitude={values.longitude}
                  location={values.location}
                  height="320px"
                  showDefaultMap
                  defaultCenter={mapCenter}
                  locationLoading={locationLoading}
                  onLocationSelect={
                    isEditMode
                      ? undefined
                      : (lat, lng, address) => {
                          setFieldValue("latitude", lat);
                          setFieldValue("longitude", lng);
                          if (address && !values.location?.trim()) {
                            setFieldValue("location", address);
                          }
                        }
                  }
                />

                <hr />
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" style={cancelButtonStyle} onClick={handleClose}>
                    Cancel
                  </button>
                  <button type="submit" style={saveButtonStyle} disabled={isSaving}>
                    {isSaving ? (
                      <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditIeComplaintForm;
